import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Alert, Button, Dialog, IconButton, CircularProgress, DialogTitle, DialogContent, DialogActions, Snackbar, Tabs, Tab } from '@mui/material';
import { protectedApi } from '../services/api';
import { useSelector, useDispatch } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import ProductForm from '../components/ProductForm';
import BannerManager from '../components/admin/BannerManager';
import MembershipManager from '../components/admin/MembershipManager';
import { fetchProducts, createProduct, updateProduct, deleteProduct } from '../store/slices/productsSlice';
import { useAuth } from '../hooks/useAuth';

const CATEGORIES = {
  harmony: { label: 'Harmony & Balance', color: 'primary' },
  prosperity: { label: 'Prosperity & Abundance', color: 'success' },
  love: { label: 'Love & Relationships', color: 'secondary' },
  healing: { label: 'Healing & Wellness', color: 'info' },
  creativity: { label: 'Creativity & Inspiration', color: 'warning' },
  protection: { label: 'Protection & Cleansing', color: 'error' },
};

export default function Admin() {
  const { user, isAdmin } = useAuth();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const products = useSelector((state) => state.products.items);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    // Store user email in localStorage if it's the admin
    if (user?.email) {
      localStorage.setItem('userEmail', user.email);
    }

    // Only fetch products if user is admin
    if (isAdmin) {
      dispatch(fetchProducts());
    }
  }, [dispatch, isAdmin, user]);

  // If user is not admin, show access denied
  if (!isAdmin) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {t('admin.unauthorized')}
        </Alert>
      </Container>
    );
  }

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProduct(null);
  };

  const handleOpenDialog = (product = null) => {
    setSelectedProduct(product);
    setOpenDialog(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteProduct(productToDelete.id)).unwrap();
      setSnackbarSeverity('success');
      setSnackbarMessage(t('admin.deleteSuccess'));
      setOpenSnackbar(true);
    } catch (error) {
      console.error('Error deleting product:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage(t('admin.deleteError'));
      setOpenSnackbar(true);
    } finally {
      setConfirmDelete(false);
      setProductToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDelete(false);
    setProductToDelete(null);
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (selectedProduct) {
        // Update existing product
        const updatedProduct = {
          ...productData,
          id: selectedProduct.id
        };
        await dispatch(updateProduct(updatedProduct)).unwrap();
        setSnackbarMessage(t('admin.updateSuccess'));
      } else {
        // Create new product
        await dispatch(createProduct(productData)).unwrap();
        setSnackbarMessage(t('admin.createSuccess'));
      }

      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving product:', err);
      setSnackbarSeverity('error');
      setSnackbarMessage(err.message || t('admin.saveError'));
      setOpenSnackbar(true);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Panel de Administración
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={t('admin.products')} />
          <Tab label={t('admin.banners')} />
          <Tab label="Membresías" />
        </Tabs>
      </Box>

      {/* Products Tab */}
      {tabValue === 0 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              {t('admin.addProduct')}
            </Button>
          </Box>

          {products.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
              }}
            >
              <Typography variant="h6" color="textSecondary">
                {t('admin.noProducts')}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Precio</TableCell>
                    <TableCell>Stock</TableCell>
                    <TableCell>Categoría</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>${product.price}</TableCell>
                      <TableCell>{product.stock}</TableCell>
                      <TableCell>
                        {product.categories?.map((category) => (
                          <Chip
                            key={category}
                            label={CATEGORIES[category]?.label || category}
                            color={CATEGORIES[category]?.color || 'default'}
                            size="small"
                            sx={{ mr: 0.5 }}
                          />
                        ))}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.is_active ? 'Activo' : 'Inactivo'}
                          color={product.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(product)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(product)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <ProductForm
            open={openDialog}
            onClose={handleCloseDialog}
            onSave={handleSaveProduct}
            product={selectedProduct}
          />
        </>
      )}

      {/* Banners Tab */}
      {tabValue === 1 && <BannerManager />}

      {/* Memberships Tab */}
      {tabValue === 2 && <MembershipManager />}

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDelete} onClose={handleCancelDelete}>
        <DialogTitle>{t('admin.confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('admin.confirmDeleteMessage', { name: productToDelete?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}
